'use client';

import React, { useState, useCallback } from 'react';
import { Calculator, Info } from 'lucide-react';

interface MortgageCalculatorProps {
  propertyPrice: number;
}

export default function MortgageCalculator({ propertyPrice }: MortgageCalculatorProps) {
  const [downPaymentPct, setDownPaymentPct] = useState(20);
  const [interestRate, setInterestRate] = useState(2.89);
  const [termYears, setTermYears] = useState(20);

  const downPayment = (propertyPrice * downPaymentPct) / 100;
  const loanAmount = propertyPrice - downPayment;
  const monthlyRate = interestRate / 100 / 12;
  const totalMonths = termYears * 12;
  const monthlyPayment = monthlyRate > 0
    ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1)
    : loanAmount / totalMonths;
  const totalPayment = monthlyPayment * totalMonths;
  const totalInterest = totalPayment - loanAmount;

  const formatTL = (val: number) =>
    '₺' + Math.round(val).toLocaleString('tr-TR');

  return (
    <div className="card-elevated rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-9 h-9 rounded-xl bg-primary-light flex items-center justify-center">
          <Calculator size={18} className="text-primary" />
        </div>
        <div>
          <h3 className="text-base font-bold text-foreground">Kredi Hesaplayıcı</h3>
          <p className="text-xs text-muted-foreground">Tahmini aylık ödeme</p>
        </div>
      </div>

      <div className="space-y-5">
        {/* Down Payment */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-foreground">Peşinat</label>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-primary tabular-nums">%{downPaymentPct}</span>
              <span className="text-xs text-muted-foreground tabular-nums">({formatTL(downPayment)})</span>
            </div>
          </div>
          <input
            type="range"
            min={10}
            max={80}
            step={5}
            value={downPaymentPct}
            onChange={(e) => setDownPaymentPct(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer"
            style={{ accentColor: 'var(--primary)' }}
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>%10</span>
            <span>%80</span>
          </div>
        </div>

        {/* Interest Rate */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-foreground">Aylık Faiz</label>
            <span className="text-sm font-bold text-primary tabular-nums">%{interestRate}</span>
          </div>
          <input
            type="range"
            min={1.5}
            max={5}
            step={0.1}
            value={interestRate}
            onChange={(e) => setInterestRate(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer"
            style={{ accentColor: 'var(--primary)' }}
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>%1.5</span>
            <span>%5.0</span>
          </div>
        </div>

        {/* Term */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-foreground">Vade</label>
            <span className="text-sm font-bold text-primary tabular-nums">{termYears} Yıl</span>
          </div>
          <div className="flex gap-2">
            {[10, 15, 20, 25, 30].map((y) => (
              <button
                key={`term-${y}`}
                onClick={() => setTermYears(y)}
                className={`flex-1 py-1.5 rounded-lg text-xs font-bold border transition-all ${termYears === y ? 'bg-primary text-white border-primary' : 'border-border text-foreground hover:border-primary hover:text-primary'}`}
              >
                {y}y
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-5 p-4 rounded-xl bg-primary-light border border-primary/20">
        <div className="text-center mb-4">
          <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-1">Tahmini Aylık Ödeme</p>
          <p className="text-3xl font-extrabold text-primary tabular-nums">{formatTL(monthlyPayment)}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Kredi Tutarı', value: formatTL(loanAmount) },
            { label: 'Toplam Ödeme', value: formatTL(totalPayment) },
            { label: 'Toplam Faiz', value: formatTL(totalInterest) },
            { label: 'Peşinat', value: formatTL(downPayment) },
          ].map((item, i) => (
            <div key={`mort-result-${i}`} className="bg-white rounded-xl p-2.5 text-center">
              <p className="text-[10px] text-muted-foreground font-medium mb-0.5">{item.label}</p>
              <p className="text-sm font-bold text-foreground tabular-nums">{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-start gap-2 mt-3">
        <Info size={12} className="text-muted-foreground flex-shrink-0 mt-0.5" />
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          Bu hesaplama tahmini olup yatırım tavsiyesi değildir. Gerçek kredi koşulları için bankanıza başvurun. DASK, konut sigortası ve vergi dahil değildir.
        </p>
      </div>
    </div>
  );
}